'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

import { useShallow } from 'zustand/react/shallow'

import { isProduction } from '@common/helpers'
import { useAuthUser } from '@common/hooks/useAuthUser'
import { isAcceptInvitationRoute } from '@common/utils/isAcceptInvitationRoute'

import { useOnboardingStore } from '@apps/onboarding/stores/onboarding.store'
import { selectAppInitShellState } from '@stores/app-init/app-init.selector'
import { useAppInitStore } from '@stores/app-init/app-init.store'
import { syncAppInitialization } from '@stores/app-init/app-init.thunks'

import { logZustandStores } from '../utils/logZustandStores'

export function useAppInitialization(isEmailVerified: boolean) {
  const pathname = usePathname()
  const { user: auth0User, isLoading: auth0Loading } = useAuthUser()
  const { isInitializing, hasInitialized, initializationError } = useAppInitStore(useShallow(selectAppInitShellState))
  const isOnboarding = useOnboardingStore((state) => state.isOnboarding)

  const isVerified = auth0User?.email_verified === true || isEmailVerified
  const isAcceptInvitation = isAcceptInvitationRoute(pathname)

  useEffect(() => {
    void syncAppInitialization({
      auth0Loading,
      auth0User: auth0User ?? null,
      isVerified,
      isAcceptInvitation,
    })
  }, [auth0Loading, auth0User, isVerified, isAcceptInvitation])

  useEffect(() => {
    const ready = !auth0Loading && !!auth0User && isVerified && !isInitializing

    if (!ready || isProduction()) return

    logZustandStores()
  }, [auth0User, auth0Loading, isVerified, isInitializing])

  const isReady = !auth0Loading && !!auth0User && hasInitialized && !isInitializing && isVerified

  return {
    isReady,
    isInitializing,
    hasInitialized,
    initializationError,
    isOnboarding,
  }
}
